import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { node, string, bool, number, func } from 'prop-types';
import { Flex, Typography, Button, AnimatedFlex } from '@mixins';
import { Loader, PrevIcon } from '@atoms';
import { OnboardingHeader, OnboardingProgress } from '@molecules';
import { theme } from '@styles';
import { getCompanyOnboarding, resetPhases } from '@store/company/duck';
import { getCurrentLocation } from '@store/location/duck';
import { routes } from '@constants';
import { ONBOARDING_STEPS } from '@constants/common';
import { SUCCESS } from '@constants/requestPhase';
import { trackEvent } from '@utils/mixpanel';

import { ReactComponent as Dropdown } from '@assets/svgs/Dropdown.svg';

const Onboarding = ({
  onSaveAndContinueLater,
  showBackButton,
  onNext,
  direction,
  image,
  title,
  children,
  showNavigationItems,
  currentStep,
  onBack,
  isNextButtonDisabled,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { user: currentUser } = useSelector(store => store.userStore);
  const { location: currentLocation } = useSelector(store => store.locationStore);
  const { onboardingState, updateCompanyOnboardingPhase } = useSelector(store => store.companyStore);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!Object.keys(onboardingState).length) {
      dispatch(getCompanyOnboarding());
    }
  }, [onboardingState]);

  useEffect(() => {
    if (currentUser?.id && !currentUser?.isOnboardingAvailable) {
      history.push(routes.home);
    }
  }, [currentUser]);

  useEffect(() => {
    if (isLoading && updateCompanyOnboardingPhase === SUCCESS) {
      dispatch(resetPhases());
      setIsLoading(false);
      history.push(routes.home);
    }
  }, [onboardingState, currentUser, updateCompanyOnboardingPhase]);

  useEffect(() => {
    if (!currentLocation && currentUser?.currentLocation) {
      dispatch(getCurrentLocation(currentUser?.currentLocation, true));
    }
  }, [currentLocation, currentUser?.currentLocation]);

  const handleSaveAndContinue = () => {
    trackEvent('Onboarding action', {
      Title: 'Save & Continue Later',
    });

    setIsLoading(true);
    if (onSaveAndContinueLater) {
      onSaveAndContinueLater();
    } else {
      setTimeout(() => {
        setIsLoading(false);
        history.push(routes.home);
      }, 500);
    }
  };

  return (
    <>
      <Flex
        px={['20px', '30px', '40px', '60px']}
        pt={showNavigationItems ? ['80px'] : ['80px', '100xp', '120px', '150px']}
        flexDirection="column"
        width="100%"
        minHeight="100vh">
        <OnboardingHeader />
        {showNavigationItems && (
          <Button
            mb={30}
            onClick={handleSaveAndContinue}
            py={10}
            px={20}
            minWidth={[161, 181, 201, 221]}
            alignSelf="end"
            variant="secondary"
            fontSize={[12, 14, 16, 18]}>
            {isLoading ? 'Saving...' : 'Save & Continue Later'}
          </Button>
        )}
        <Flex
          flexDirection="column"
          flex="1"
          width="100%"
          alignItems="center"
          overflowY="auto"
          pb={showNavigationItems || showBackButton ? [180, 200] : ['30px', '40px']}
          mt={0}>
          <AnimatedFlex
            initial={{ opacity: 0, x: direction === 'forward' ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{
              opacity: 0,
              x: direction === 'forward' ? -50 : 50,
              transition: { x: { duration: 0.5 }, opacity: { duration: 0.5 } },
            }}
            transition={{ x: { delay: 0.4, duration: 0.5 }, opacity: { delay: 0.4, duration: 0 } }}
            width="100%"
            maxWidth="1320px"
            flexDirection={['column', 'column', 'row']}
            alignItems={['center', 'center', 'flex-start']}
            justifyContent="space-between"
            gap={[40, 60, 80]}>
            <Flex
              width="100%"
              maxWidth={image ? ['100%', '100%', '70%', '735px'] : '100%'}
              flexDirection="column"
              mb={[40, 40, 0, 0]}
              mr={[0, 0, 40, 40]}>
              {title && (
                <Typography
                  whiteSpace="pre-line"
                  variant="rightserif-400"
                  fontSize={['30px', '44px', '50px', '60px']}
                  lineHeight={1.1}
                  mb={10}
                  color={theme.color.gray[900]}>
                  {title}
                </Typography>
              )}
              <Flex width="100%" flexDirection="column">
                {children}
              </Flex>
            </Flex>

            {image && (
              <Flex
                background={`url(${image}) center`}
                backgroundSize="cover"
                minWidth={[0, 0, 250, 400]}
                height={[0, 0, 260, 420]}
              />
            )}
          </AnimatedFlex>
        </Flex>

        {showNavigationItems && (
          <OnboardingProgress
            isNextButtonDisabled={isNextButtonDisabled}
            direction={direction}
            totalSteps={onboardingState?.availableSteps?.filter(step => step !== ONBOARDING_STEPS.WELCOME).length ?? 6}
            currentStep={currentStep}
            onBack={onBack}
            onNext={onNext}
          />
        )}
        {showBackButton && (
          <Flex
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            width="100%"
            px={['20px', '30px', '40px', '60px']}
            pt="10px"
            pb={['15px', '20px', '30px', '40px']}
            flexDirection="column"
            alignItems="center"
            backgroundColor={theme.color.white}>
            <Flex width="100%" maxWidth="1320px" alignItems="center" justifyContent="space-between">
              <Button fontSize={[12, 14, 16, 18]} onClick={onBack}>
                <PrevIcon SVG={Dropdown} color={theme.color.mintMinded[900]} height={['8px', '12px']} mr="8px" />
                Back
              </Button>
            </Flex>
          </Flex>
        )}
      </Flex>
      {Object.keys(currentUser).length === 1 && (
        <Loader position="fixed" background={theme.color.white} width={64} height={64} />
      )}
    </>
  );
};

Onboarding.defaultProps = {
  showBackButton: false,
  isNextButtonDisabled: false,
  direction: null,
  image: null,
  title: null,
  showNavigationItems: false,
  currentStep: 1,
  onSaveAndContinueLater: null,
};

Onboarding.propTypes = {
  onSaveAndContinueLater: func,
  showBackButton: bool,
  isNextButtonDisabled: bool,
  onNext: func.isRequired,
  direction: string,
  image: string,
  title: string,
  children: node.isRequired,
  showNavigationItems: bool,
  currentStep: number,
  onBack: func.isRequired,
};

export default Onboarding;
